import type { RouterHistory } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense } from "react";

import { authEnabled, clerkPublishableKey } from "./config";
import { LocalAuthProvider } from "./local-provider";

// Only loaded when the flag is on, so ClerkJS never ships in the local build.
const ClerkAuthProvider = lazy(() =>
  import("./clerk/provider").then((m) => ({ default: m.ClerkAuthProvider })),
);

export interface AuthProviderProps {
  /** The router's history: Clerk navigates through it (sign-in redirects, sign-out). */
  history: RouterHistory;
  children: ReactNode;
  /** Test seam; defaults to `VITE_AUTH_ENABLED`. */
  enabled?: boolean;
  /** Test seam; defaults to `VITE_CLERK_PUBLISHABLE_KEY`. */
  publishableKey?: string | null;
}

/**
 * Picks the provider on the flag (`docs/HOSTING_PLAN.md` "Frontend"): the local stub when auth
 * is off, Clerk when it is on. A missing publishable key with the flag on is a build error
 * surfaced at runtime, not a silent fallback to local mode.
 */
export function AuthProvider({
  history,
  children,
  enabled = authEnabled(),
  publishableKey = clerkPublishableKey(),
}: AuthProviderProps) {
  if (!enabled) return <LocalAuthProvider>{children}</LocalAuthProvider>;
  if (!publishableKey) {
    throw new Error("VITE_AUTH_ENABLED is on but VITE_CLERK_PUBLISHABLE_KEY is not set.");
  }
  return (
    <Suspense fallback={<AuthLoading />}>
      <ClerkAuthProvider publishableKey={publishableKey} history={history}>
        {children}
      </ClerkAuthProvider>
    </Suspense>
  );
}

/** The blank page shown while ClerkJS loads and reports (`flows.md` §1 "header skeleton"). */
export function AuthLoading() {
  return <main id="main" aria-busy="true" className="min-h-[50vh]" data-testid="auth-loading" />;
}
